import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, MessageSquare, Paperclip, FileText, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar, EmptyState, VipBadge } from "@/components/common";
import { useApi, errMsg, useFileUrl } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { timeAgo } from "@/lib/labels";

function Attachment({ att, mine, onLoad }) {
  const api = useApi();
  const isImg = att.content_type?.startsWith("image/");
  const url = useFileUrl(isImg ? att.path : null);
  const download = async () => {
    try {
      const blob = await api("get", `/files/${att.path}`, null, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = att.filename || "file"; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (isImg) return url
    ? <button type="button" onClick={download} className="block mb-1" data-testid="chat-attachment-image"><img src={url} alt={att.filename} onLoad={onLoad} className="rounded max-h-64 max-w-full object-cover" /></button>
    : <div className="w-48 h-32 rounded bg-slate-200/60 animate-pulse mb-1" />;
  return (
    <button type="button" onClick={download} data-testid="chat-attachment-file"
      className={`flex items-center gap-2 mb-1 px-3 py-2 rounded border text-left ${mine ? "border-sky-400 bg-sky-700/40" : "border-slate-200 bg-slate-50"}`}>
      <FileText size={18} /><span className="truncate max-w-[200px] font-medium">{att.filename}</span>
    </button>
  );
}

function ConversationList({ activeId }) {
  const api = useApi();
  const [convs, setConvs] = useState(null);
  useEffect(() => {
    let off = false;
    const tick = () => api("get", "/conversations").then((d) => !off && setConvs(d)).catch((err) => console.error("Conversations poll failed:", err));
    tick();
    const t = setInterval(tick, 5000);
    return () => { off = true; clearInterval(t); };
  }, [api]);
  if (convs === null) return <p className="p-5 text-sm text-slate-500">Loading...</p>;
  if (!convs.length) return <p className="p-5 text-sm text-slate-500" data-testid="conversations-empty">No conversations yet. Open someone's profile and click “Message”.</p>;
  return (
    <ul data-testid="conversation-list">
      {convs.map((c) => (
        <li key={c.user_id}>
          <Link to={`/messages/${c.user_id}`} data-testid={`conversation-item-${c.user_id}`}
            className={`flex gap-3 p-4 border-b border-slate-100 transition-colors ${activeId === c.user_id ? "bg-sky-50" : "hover:bg-slate-50"}`}>
            <UserAvatar user={c.user} size={42} />
            <div className="min-w-0 flex-1">
              <div className="flex justify-between gap-2">
                <p className={`truncate ${c.unread ? "font-bold" : "font-semibold"}`}>{c.user.name}</p>
                <span className="font-mono text-[10px] text-slate-400 shrink-0">{timeAgo(c.last_message.created_at)}</span>
              </div>
              <div className="flex justify-between gap-2 items-center">
                <p className={`text-sm truncate ${c.unread ? "text-slate-900" : "text-slate-500"}`}>{c.last_message.text || (c.last_message.attachment ? `Attachment: ${c.last_message.attachment.filename}` : "")}</p>
                {c.unread > 0 && <span data-testid={`conversation-unread-${c.user_id}`} className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-slate-900 text-[11px] font-bold flex items-center justify-center">{c.unread}</span>}
              </div>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Chat({ uid }) {
  const api = useApi();
  const nav = useNavigate();
  const { me } = useMe();
  const [other, setOther] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const lastRef = useRef(null);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setMsgs([]); lastRef.current = null;
    api("get", `/users/${uid}`).then(setOther).catch(() => { toast.error("User not found"); nav("/messages"); });
    let off = false;
    const tick = async () => {
      try {
        const q = lastRef.current ? `?after=${encodeURIComponent(lastRef.current)}` : "";
        const fresh = await api("get", `/messages/${uid}${q}`);
        if (off || !fresh.length) return;
        lastRef.current = fresh[fresh.length - 1].created_at;
        setMsgs((m) => { const ids = new Set(m.map((x) => x.id)); return [...m, ...fresh.filter((x) => !ids.has(x.id))]; });
      } catch (err) { console.error("Message poll failed:", err); }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => { off = true; clearInterval(t); };
  }, [uid, api, nav]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs.length]);

  const pushMine = (m) => {
    setMsgs((prev) => prev.some((x) => x.id === m.id) ? prev : [...prev, m]);
    lastRef.current = m.created_at;
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      pushMine(await api("post", `/messages/${uid}`, { text }));
      setText("");
    } catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
  };

  const sendFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File larger than 10 MB");
    const fd = new FormData(); fd.append("file", file); fd.append("text", text);
    setSending(true);
    try { pushMine(await api("post", `/messages/${uid}/attachment`, fd)); setText(""); }
    catch (err) { toast.error(errMsg(err)); } finally { setSending(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="flex flex-col h-full w-full min-w-0">
      <div className="flex items-center gap-3 p-4 border-b border-slate-200 bg-white">
        <Link to="/messages" className="md:hidden text-slate-500" data-testid="chat-back-button"><ArrowLeft size={20} /></Link>
        {other && (
          <Link to={`/u/${uid}`} className="flex items-center gap-3 min-w-0" data-testid="chat-header-profile-link">
            <UserAvatar user={other} size={38} />
            <div className="min-w-0">
              <p className="font-bold truncate flex items-center gap-2" data-testid="chat-header-name">{other.name}<VipBadge user={other} /></p>
              <p className="text-xs text-slate-500 truncate">{other.role === "company" ? "Company" : other.headline || "Technician"}</p>
            </div>
          </Link>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50" data-testid="chat-messages">
        {msgs.length === 0 && <p className="text-center text-sm text-slate-400 py-10">Start the conversation.</p>}
        {msgs.map((m) => {
          const mine = m.sender_id === me.user_id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div data-testid={`chat-message-${m.id}`} className={`max-w-[80%] px-3.5 py-2 rounded-lg text-sm whitespace-pre-line ${mine ? "bg-sky-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                {m.attachment && <Attachment att={m.attachment} mine={mine} onLoad={() => endRef.current?.scrollIntoView({ block: "end" })} />}
                {m.text}
                <p className={`mt-1 font-mono text-[10px] ${mine ? "text-sky-100" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {!me.is_vip ? (
        <div className="p-4 border-t border-slate-200 bg-amber-50 flex flex-wrap items-center justify-between gap-3" data-testid="chat-vip-required">
          <p className="text-sm text-slate-800 flex items-center gap-2"><Crown size={16} className="text-amber-600" />Sending messages is a VIP feature.</p>
          <Button asChild size="sm" className="bg-amber-500 text-slate-950 hover:bg-amber-400" data-testid="chat-get-vip-button"><Link to="/vip">Get VIP</Link></Button>
        </div>
      ) : (
      <form onSubmit={send} className="p-3 border-t border-slate-200 bg-white flex gap-2 items-end">
        <input ref={fileRef} type="file" hidden accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => sendFile(e.target.files[0])} data-testid="chat-file-input" />
        <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0" disabled={sending} onClick={() => fileRef.current.click()} data-testid="chat-attach-button" aria-label="Attach photo or PDF">
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
        </Button>
        <Textarea data-testid="chat-input-message" rows={1} className="min-h-[44px] max-h-32 resize-none" placeholder="Write a message..." value={text}
          onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <Button type="submit" size="icon" className="h-11 w-11 shrink-0" disabled={sending || !text.trim()} data-testid="chat-send-button" aria-label="Send"><Send size={18} /></Button>
      </form>
      )}
    </div>
  );
}

export default function Messages() {
  const { uid } = useParams();
  return (
    <div className="panel overflow-hidden grid md:grid-cols-[320px_1fr] h-[calc(100vh-12rem)] md:h-[calc(100vh-9rem)]">
      <aside className={`border-r border-slate-200 overflow-y-auto ${uid ? "hidden md:block" : ""}`}>
        <div className="p-4 border-b border-slate-200"><p className="eyebrow">Messenger</p><h1 className="font-extrabold text-xl mt-1">Messages</h1></div>
        <ConversationList activeId={uid} />
      </aside>
      <section className={`${uid ? "flex" : "hidden md:flex"} min-h-0`}>
        {uid ? <Chat key={uid} uid={uid} /> : (
          <div className="flex-1 flex items-center justify-center p-8">
            <EmptyState testId="chat-empty" title="Select a conversation" text="Or message someone from their profile." action={<MessageSquare className="mx-auto text-slate-300" size={36} />} />
          </div>
        )}
      </section>
    </div>
  );
}
